import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  ArrowRight,
  BarChart3,
  Download,
  Gauge,
  History,
  PieChart,
  Smartphone,
  Users,
  Vault,
  WalletCards,
  Zap,
} from 'lucide-react';
import '@fontsource-variable/manrope/wght.css';

import dashboardImage from './assets/dashboard-light.webp';
import historyImage from './assets/history-light.webp';
import limitsImage from './assets/limits-light.webp';
import membersImage from './assets/members-light.webp';
import reportsImage from './assets/reports-light.webp';
import transferImage from './assets/transfer-light.webp';
import './landing.css';

const APP_URL = 'https://app.kazna.space';
const ANDROID_APK_URL = '/downloads/Kazna.apk';
const LOGO_URL = '/icon.svg';

const benefits = [
  {
    title: 'Вместе',
    text: 'Изменения доступны всем участникам',
    icon: Users,
  },
  {
    title: 'Быстро',
    text: 'Добавляйте доходы и расходы в пару касаний',
    icon: Zap,
  },
  {
    title: 'Аналитика',
    text: 'Смотрите расходы по категориям',
    icon: PieChart,
  },
];

const features = [
  {
    title: 'Вся семья — в одной Казне',
    text: 'Приглашайте близких, назначайте роли и ведите общий бюджет вместе.',
    image: membersImage,
    imageAlt: 'Участники общего пространства Казны',
    icon: Users,
  },
  {
    title: 'Все финансы на одном экране',
    text: 'Доходы, расходы и остаток всегда видны на понятной панели.',
    image: dashboardImage,
    imageAlt: 'Панель семейного бюджета Казны',
    icon: WalletCards,
  },
  {
    title: 'История доходов и расходов',
    text: 'Быстро находите нужные операции за любой период.',
    image: historyImage,
    imageAlt: 'История доходов и расходов в Казне',
    icon: History,
  },
  {
    title: 'Наглядная аналитика',
    text: 'Сравнивайте периоды и находите категории с самыми большими расходами.',
    image: reportsImage,
    imageAlt: 'Отчёты и аналитика расходов в Казне',
    icon: BarChart3,
  },
  {
    title: 'Лимиты по категориям',
    text: 'Контролируйте приближение к месячному лимиту и избегайте лишних трат.',
    image: limitsImage,
    imageAlt: 'Лимиты расходов по категориям в Казне',
    icon: Gauge,
  },
  {
    title: 'Переводы между бюджетами',
    text: 'Переносите деньги между бюджетами без ручных расчётов.',
    image: transferImage,
    imageAlt: 'Перевод денег между бюджетами в Казне',
    icon: Vault,
  },
];

function Phone({
  className,
  image,
  alt,
  loading,
}: {
  className: string;
  image: string;
  alt: string;
  loading?: 'eager' | 'lazy';
}) {
  return (
    <div className={className}>
      <span className="device-speaker" aria-hidden="true" />
      <img src={image} alt={alt} width="640" height="1428" loading={loading} />
    </div>
  );
}

function Landing() {
  return (
    <main>
      <div className="site-background" aria-hidden="true" />
      <div className="hero-stage">
        <header className="landing-nav">
          <a className="landing-brand" href="#top" aria-label="Казна, в начало">
            <img src={LOGO_URL} width="42" height="42" alt="" aria-hidden="true" />
            <span>Казна</span>
          </a>
          <a className="nav-action" href={APP_URL}>
            Открыть Казну <ArrowRight size={17} />
          </a>
        </header>

        <section className="hero" id="top">
          <div className="hero-copy">
            <p className="landing-eyebrow">Планируйте вместе</p>
            <h1>
              <span>Деньги семьи —</span><br />
              <em>в одной Казне.</em>
            </h1>
            <p className="hero-lead">
              Один бюджет для всей семьи. Расходы, планы и аналитика всегда под рукой.
            </p>
            <div className="hero-actions">
              <a className="primary-action" href={APP_URL}>
                Открыть Казну <ArrowRight size={20} />
              </a>
              <a
                className="secondary-action"
                href={ANDROID_APK_URL}
                download="Kazna.apk"
              >
                Скачать тестовую APK <Download size={20} />
              </a>
            </div>
            <p className="hero-pricing">14 дней без карты, затем 590 ₽/месяц с каждого пользователя, включая каждого участника семьи.</p>
          </div>

          <div className="hero-visual">
            <div className="hero-orbit" aria-hidden="true" />
            <div className="hero-app-mark" aria-hidden="true">
              <img src={LOGO_URL} width="70" height="70" alt="" />
            </div>
            <Phone
              className="hero-device"
              image={dashboardImage}
              alt="Панель семейного бюджета Казны"
              loading="eager"
            />
            <div className="hero-benefits" aria-label="Преимущества Казны">
              {benefits.map((benefit, index) => (
                <article className={`hero-benefit-card hero-benefit-${index + 1}`} key={benefit.title}>
                  <benefit.icon size={25} aria-hidden="true" />
                  <span>
                    <strong>{benefit.title}.</strong>
                    <small>{benefit.text}</small>
                  </span>
                </article>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="features" id="features">
        <div className="section-heading">
          <p className="landing-eyebrow">Все возможности — в одном приложении</p>
          <h2>Понятно с первого экрана</h2>
          <p>
            Удобный интерфейс, понятная навигация и всё необходимое для контроля
            семейного бюджета.
          </p>
        </div>

        <div className="feature-grid">
          {features.map((feature, index) => (
            <article className="feature-card" key={feature.title}>
              <div className="feature-copy">
                <div className="feature-meta">
                  <span className="feature-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="feature-icon" aria-hidden="true">
                    <feature.icon size={21} />
                  </span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>
              <Phone
                className="feature-device"
                image={feature.image}
                alt={feature.imageAlt}
                loading="lazy"
              />
            </article>
          ))}
        </div>
      </section>

      <section className="install" id="install">
        <div className="install-heading">
          <p className="landing-eyebrow">Всегда под рукой</p>
          <h2>Установите Казну на телефон</h2>
          <p>Откройте приложение из браузера и добавьте его на главный экран.</p>
        </div>

        <div className="install-platform-grid">
          <article className="install-platform-card">
            <div className="platform-copy">
              <span className="platform-icon" aria-hidden="true">
                <Smartphone size={25} />
              </span>
              <p className="platform-label">Установка через Safari</p>
              <h3>iPhone</h3>
              <ol>
                <li><span>1</span>Откройте Казну в Safari</li>
                <li><span>2</span>Нажмите «Поделиться»</li>
                <li><span>3</span>Выберите «На экран Домой»</li>
              </ol>
              <a className="text-action" href={APP_URL}>
                Открыть в браузере <ArrowRight size={17} />
              </a>
            </div>
            <div className="qr-panel">
              <img src="/qr-app.png" width="180" height="180" alt="QR-код для открытия app.kazna.space на iPhone" />
              <span>app.kazna.space</span>
            </div>
          </article>

          <article className="install-platform-card">
            <div className="platform-copy">
              <span className="platform-icon android" aria-hidden="true">
                <Smartphone size={25} />
              </span>
              <p className="platform-label">Установка через браузер</p>
              <h3>Android</h3>
              <ol>
                <li><span>1</span>Откройте Казну в браузере</li>
                <li><span>2</span>Нажмите меню ⋮</li>
                <li><span>3</span>Выберите «Установить приложение» или «Добавить на главный экран»</li>
              </ol>
              <div className="platform-actions">
                <a className="text-action" href={APP_URL}>
                  Открыть в браузере <ArrowRight size={17} />
                </a>
                <a className="apk-action" href={ANDROID_APK_URL} download="Kazna.apk">
                  Скачать тестовую APK <Download size={16} />
                </a>
              </div>
            </div>
            <div className="qr-panel">
              <img src="/qr-app.png" width="180" height="180" alt="QR-код для открытия app.kazna.space на Android" />
              <span>app.kazna.space</span>
            </div>
          </article>
        </div>
      </section>

      <section className="final-cta">
        <span className="cta-mark" aria-hidden="true">
          <img src={LOGO_URL} width="84" height="84" alt="" />
        </span>
        <h2>Начните вести семейный бюджет уже сегодня</h2>
        <p>Создайте пространство, пригласите близких и ведите финансы вместе.</p>
        <p>14 дней без карты. Затем 590 ₽ в месяц за каждого пользователя. Автоматических списаний нет.</p>
        <a className="primary-action light" href={APP_URL}>
          Открыть Казну <ArrowRight size={20} />
        </a>
      </section>

      <footer>
        <a className="landing-brand" href="#top" aria-label="Казна, в начало">
          <img src={LOGO_URL} width="42" height="42" alt="" aria-hidden="true" />
          <span>Казна</span>
        </a>
        <p>Совместный семейный бюджет</p>
        <a href={APP_URL}>app.kazna.space</a>
      </footer>
    </main>
  );
}

createRoot(document.getElementById('landing-root')!).render(
  <StrictMode>
    <Landing />
  </StrictMode>,
);
